import { nextTick } from "vue";
import { iframeMutations } from "@/shared/store/iframe.mutations.js";
import { coffeeCartService } from "../services/coffee-cart.service.js";
import { coffeeUploadsService } from "../services/coffee-uploads.service.js";
import { copy, parseHtml } from "@/shared/utils/string.js";
import { waitForMe, cloneDeep } from "@/shared/utils/helpers";
import { coffeeSnapshotService } from "@/apps/coffee/store/coffee-snapshot.service";
import { t } from "@/i18n";

export default {
    namespaced: true,
    state: () => ({
        coffee: {},
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.coffee,
        easyCartUrl: (s, g, rootS, rootGetters) => rootGetters.easyCartUrl,
    },
    mutations: {
        ...iframeMutations,
        setProject(state, { coffee, snapshot = {} }) {
            state.coffee = coffeeSnapshotService.makeSnapshotState(
                state,
                "coffee",
                coffee,
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.coffee = {};
            coffeeSnapshotService.clearSnapshot(state, "coffee", {});
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const coffee = cloneDeep(state.coffee);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && coffee.id) {
                state.iframe.contentWindow.postMessage({ coffeeData: coffee }, getters.appUrl);
            }
        },
        async getECProducts({ rootState, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            rootState.waiting = true;
            const data = await coffeeCartService.getECProducts();
            rootState.waiting = false;
            return data;
        },
        async getECProduct({ rootState }, ecProductId) {
            rootState.waiting = true;

            const data = await coffeeCartService.getECProduct(ecProductId);
            rootState.waiting = false;
            return data;
        },
        async connectWithECProduct({ state }, productId) {
            state.coffee.ecProductId = productId;
        },
        async setDynamicPrice({ state, rootState }, isEnabled) {
            rootState.waiting = true;
            try {
                await coffeeCartService.setDynamicPrice({
                    id: state.coffee.ecProductId,
                    enabled: Boolean(isEnabled),
                });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        generateScript2({ state, rootState, getters }) {
            const functionModule = require("@/apps/coffee/config/coffee.js");
            let functionText = functionModule.toString();

            const iframeDocument = state.iframe.contentDocument;
            const adjust = iframeDocument.querySelector("[data-ecoffee-adjust]");
            if (!adjust.classList.contains("hidden")) {
                adjust.classList.add("hidden");
                adjust.classList.add("opacity-0");
            }

            const coffeeHtml = JSON.stringify(
                iframeDocument.querySelector("[data-ecoffee-container]").outerHTML
            );

            functionText = functionText.replace("fontNameToReplace", state.coffee.fontFamily);
            functionText = functionText.replace('"coffeeHtmlToReplace"', coffeeHtml);
            functionText = functionText.replace("webhookUrlToReplace", state.coffee.webhookUrl);
            functionText = functionText.replace("coffeeIdToReplace", state.coffee.id);
            functionText = functionText.replace("originToReplace", rootState.globalUrl);
            functionText = functionText.replace("clientUrlToReplace", rootState.globalUrl);
            functionText = functionText.replace("ecProductIdToReplace", state.coffee.ecProductId);
            functionText = functionText.replace("sendEmailsToReplace", state.coffee.sendEmails);
            functionText = functionText.replace("easyCartUrlToReplace", getters.easyCartUrl);
            functionText = functionText.replace("langToReplace", rootState.lang);
            functionText = functionText.replace("mainColorToReplace", state.coffee.mainColor);
            functionText = functionText.replace(
                "buttonTextColorToReplace",
                state.coffee.buttonTextColor
            );

            functionText = `(${functionText})();`;
            return functionText;
        },
        publishProject2({ rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            dispatch("hostCode2");
        },
        async unpublishCoffee({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.coffee.uuid });
            commit("setUnpublished", state.coffee, { root: true });
            dispatch("coffee/projects/mergeSnapshotChanges", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        unpublish({ _ }, { uuid }) {
            return coffeeUploadsService.hostCode({
                code: "",
                uuid,
                publish: false,
            });
        },
        showCodeModal({ state, rootState, commit }) {
            const scriptLink = `<script src="${rootState.globalUrl}/coffees/scripts/${state.coffee.uuid}.js" type="text/javascript"></script>`;

            const modalData = {
                type: "code",
                title: t("coffee.code.title"),
                desc: t("coffee.code.desc"),
                code: scriptLink,
                actionBtn: t("shared.copy_code"),
                callback: () => {
                    copy(scriptLink);
                    commit(
                        "showAlert",
                        {
                            type: "success",
                            title: t("shared.code_copied"),
                        },
                        { root: true }
                    );
                },
            };
            commit("showModal", modalData, { root: true });
        },
        async hostCode2({ state, rootState, rootGetters, getters, dispatch, commit }) {
            rootState.waiting = true;

            state.iframe.contentWindow.postMessage({ showPreview: "off" }, getters.appUrl);

            await waitForMe(100);
            await nextTick();
            const { coffee = {} } = state;
            const { showAmountInput, ecProductId } = coffee || {};

            // @fix: publishing without the product does not make any sense...
            if (!ecProductId) {
                return commit(
                    "showAlert",
                    {
                        type: "warning",
                        title: t("coffee.product.cannot_publish_until_connected"),
                    },
                    { root: true }
                );
            }
            // @fix: SETTLE dynamic Price, only WHEN PUBLISH | to assure PROD__ready!
            await dispatch("setDynamicPrice", showAmountInput);

            const firstPublish = !rootGetters.isPublished(coffee);

            const scriptText = await dispatch("generateScript2");

            const code = parseHtml(scriptText);
            const updatedCoffee = await coffeeUploadsService.hostCode({
                code,
                uuid: state.coffee.uuid,
                publish: true,
            });

            state.coffee.code = updatedCoffee.code;
            state.coffee.access = updatedCoffee.access;

            await dispatch("coffee/projects/mergeSnapshotChanges", null, { root: true });

            if (firstPublish) {
                dispatch("showCodeModal");
            }

            state.iframe.contentWindow.postMessage({ showPreview: "on" }, getters.appUrl);

            rootState.waiting = false;
        },
        changeFontFamily({ state }, payload) {
            state.coffee.fontFamily = payload;
        },
        async readLogoFile({ state, rootState }, payload) {
            rootState.waiting = true;
            try {
                const { logo } = await coffeeUploadsService.coffeeLogo({
                    file: payload.file,
                    coffeeId: state.coffee.id,
                });

                state.coffee.logo = logo;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
    },
};
